import { indexBy, prop, omit } from 'ramda';

import { createReducer } from 'utils/ReduxUtils';

import * as actionTypes from '../../actionTypes';

/**  @typedef {Record<string, import('modules/audits/types/stored-audit').StoredAudit>} AuditsById */

/** @type {AuditsById} */
const initialState = {};

const extractId = item => (typeof item === 'object' && 'id' in item ? item.id : item);

export const auditCleaner = audit => ({
  ...omit(['links', 'dealer', 'modifiedBy', 'createdBy'], audit),
  ...('dealer' in audit ? { dealerId: audit.dealer.id } : {}),
  createdBy: extractId(audit.createdBy),
  modifiedBy: extractId(audit.modifiedBy),
  vehicleInfo: audit.vehicleInfo ?? {},
  customerInfo: audit.customerInfo ?? {},
  authorId: extractId(audit.author),
});

const updateAudits = (state, { payload }) => ({
  ...state,
  ...indexBy(prop('id'))(payload.content.map(audit => auditCleaner({ ...state?.[audit.id], ...audit }))),
});

const updateOne = (state, { payload }) => ({
  ...state,
  [payload.id]: {
    ...(state?.[payload.id] ?? {}),
    ...auditCleaner(payload),
  },
});

const deleteOne = (state, { payload: id }) => ({
  ...omit([id], state),
});

const addMarker = (state, { payload }) => {
  const { auditId, sectionId, questionId, marker } = payload;
  const audit = { ...(state[auditId] ?? {}) };
  const formattedMarker = { ...marker, isNew: true };

  const sectionIndex = audit.sections?.findIndex(s => s.id === sectionId) ?? -1;
  if (sectionIndex === -1) {
    return state;
  }

  const section = { ...audit.sections[sectionIndex] };
  const questionIndex = section.questions?.findIndex(q => q.id === questionId) ?? -1;
  if (questionIndex === -1) {
    return state;
  }

  const question = { ...section.questions[questionIndex] };
  question.markers = [...(question.markers ?? []), formattedMarker];

  section.questions = [...section.questions];
  section.questions[questionIndex] = question;
  audit.sections = [...audit.sections];
  audit.sections[sectionIndex] = section;

  return updateOne(state, { payload: audit });
};

const updateMarker = (state, { payload }) => {
  const { auditId, sectionId, questionId, marker } = payload;
  const audit = { ...(state[auditId] ?? {}) };

  const sectionIndex = audit.sections?.findIndex(s => s.id === sectionId) ?? -1;
  if (sectionIndex === -1) {
    return state;
  }

  const section = { ...audit.sections[sectionIndex] };
  const questionIndex = section.questions?.findIndex(q => q.id === questionId) ?? -1;
  if (questionIndex === -1) {
    return state;
  }

  const question = { ...section.questions[questionIndex] };
  const markerIndex = question.markers.findIndex(({ id }) => id === marker.id);
  if (markerIndex > -1) {
    const newMarker = {
      ...question.markers[markerIndex],
      ...marker,
    };

    question.markers = [...question.markers];
    question.markers[markerIndex] = newMarker;
  }

  section.questions = [...section.questions];
  section.questions[questionIndex] = question;
  audit.sections = [...audit.sections];
  audit.sections[sectionIndex] = section;

  return updateOne(state, { payload: audit });
};

const deleteMarker = (state, { payload }) => {
  const { auditId, sectionId, questionId, markerId } = payload;
  const audit = { ...(state[auditId] ?? {}) };

  const sectionIndex = audit.sections?.findIndex(s => s.id === sectionId) ?? -1;
  if (sectionIndex === -1) {
    return state;
  }

  const section = { ...audit.sections[sectionIndex] };
  const questionIndex = section.questions?.findIndex(q => q.id === questionId) ?? -1;
  if (questionIndex === -1) {
    return state;
  }

  const question = { ...section.questions[questionIndex] };
  question.markers = [...(question.markers ?? [])];

  const markerIndex = question.markers.findIndex(m => m.id === markerId);
  question.markers.splice(markerIndex, 1);

  section.questions = [...section.questions];
  section.questions[questionIndex] = question;
  audit.sections = [...audit.sections];
  audit.sections[sectionIndex] = section;

  return updateOne(state, { payload: audit });
};

const setFromLinkedAudits = (state, { payload }) => payload.sameReferenceAudits.reduce((acc, audit) => updateOne(acc, { payload: audit }), state);

/** @type {import('redux').Reducer<AuditsById>} */
export default createReducer(initialState, {
  [actionTypes.FETCH_AUDITS_SUCCESS]: updateAudits,
  [actionTypes.FETCH_AUDIT_SUCCESS]: updateOne,
  [actionTypes.PUT_SUCCESS]: updateOne,
  [actionTypes.POST_SUCCESS]: updateOne,
  [actionTypes.POST_CLONE_SUCCESS]: updateOne,
  [actionTypes.PATCH_STATUS_SUCCESS]: updateOne,
  [actionTypes.START_SUCCESS]: updateOne,
  [actionTypes.DELETE_SUCCESS]: deleteOne,
  [actionTypes.POST_MARKER_SUCCESS]: addMarker,
  [actionTypes.PATCH_MARKER_SUCCESS]: updateMarker,
  [actionTypes.DELETE_MARKER_SUCCESS]: deleteMarker,
  [actionTypes.FETCH_LINKED_AUDITS_SUCCESS]: setFromLinkedAudits,
});
